import { Loader2 } from 'lucide-react';
import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorState, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { SocialProvider } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useAdAccounts } from '../hooks/use-ad-accounts';
import { adAccountPickSchema, type AdAccountPickForm } from '../schemas/ad-account-schema';

export interface AdAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  provider: SocialProvider;
  /** The account already picked, preselected when changing. */
  currentId: string | null;
  pending: boolean;
  error: unknown;
  onConfirm: (values: AdAccountPickForm) => void;
}

/** Lists the ad accounts the connected account can read and confirms one. */
export function AdAccountDialog({
  open,
  onOpenChange,
  storeId,
  provider,
  currentId,
  pending,
  error,
  onConfirm,
}: AdAccountDialogProps) {
  const { t } = useTranslation(['social', 'common']);
  const message = useErrorMessage();
  const idPrefix = useId();
  const accounts = useAdAccounts(storeId, provider, open);
  const form = useForm<AdAccountPickForm>({
    resolver: useI18nResolver(adAccountPickSchema),
    defaultValues: { adAccountId: currentId ?? '' },
  });
  const { reset } = form;

  useEffect(() => {
    if (open) reset({ adAccountId: currentId ?? '' });
  }, [open, currentId, reset]);

  const submit = form.handleSubmit((values) => onConfirm(values));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('social:adAccountDialog.title')}</DialogTitle>
          <DialogDescription>{t('social:adAccountDialog.description')}</DialogDescription>
        </DialogHeader>
        {accounts.isPending ? (
          <p role="status" className="text-muted-foreground flex items-center gap-2 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            {t('social:adAccountDialog.loading')}
          </p>
        ) : accounts.error ? (
          <ErrorState error={accounts.error} onRetry={() => void accounts.refetch()} compact />
        ) : accounts.data.items.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('social:adAccountDialog.empty')}</p>
        ) : (
          <Form {...form}>
            <form onSubmit={submit} noValidate className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="adAccountId"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <RadioGroup
                        value={field.value}
                        onValueChange={field.onChange}
                        aria-label={t('social:adAccountDialog.title')}
                        className="flex flex-col gap-2"
                      >
                        {accounts.data.items.map((account) => (
                          <label
                            key={account.id}
                            htmlFor={`${idPrefix}-${account.id}`}
                            className={cn(
                              'flex cursor-pointer items-start gap-3 rounded-md border p-3',
                              field.value === account.id && 'border-primary bg-primary-tint/40',
                            )}
                          >
                            <RadioGroupItem
                              id={`${idPrefix}-${account.id}`}
                              value={account.id}
                              className="mt-0.5"
                            />
                            <span className="grid gap-0.5 text-sm">
                              <span className="font-medium">{account.name}</span>
                              <span className="text-muted-foreground font-mono text-xs">
                                {[account.id, account.currency, account.country]
                                  .filter(Boolean)
                                  .join(' · ')}
                              </span>
                            </span>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {error ? (
                <Alert variant="destructive" role="alert">
                  <AlertDescription>{message(error)}</AlertDescription>
                </Alert>
              ) : null}
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  disabled={pending}
                  onClick={() => onOpenChange(false)}
                >
                  {t('common:actions.cancel')}
                </Button>
                <Button type="submit" disabled={pending}>
                  {pending
                    ? t('social:adAccountDialog.submitting')
                    : t('social:adAccountDialog.submit')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
